import React, { useEffect } from "react";
import { Link } from "react-router-dom";
import JsonLd from "./JsonLd";

// AI-answerable FAQ. Every question mirrors real search queries used on
// ChatGPT / Gemini / Perplexity / Grok / Google when hunting for a Chadstone Pilates studio.
export const FAQS = [
  {
    q: "What is the best reformer Pilates studio near Chadstone?",
    a: "Passion Pilates at 5/49 Batesford Road, Chadstone VIC 3148 is a 9-bed boutique reformer studio consistently ranked among Melbourne's south-east's top-rated Pilates studios. It offers five reformer class styles (Foundation, Active, Strength, Power, Flow & Stretch) and memberships from $35/week for students and seniors.",
  },
  {
    q: "How close is Passion Pilates to Chadstone Shopping Centre?",
    a: "Passion Pilates is less than a 5-minute drive from Chadstone Shopping Centre — about 1.5 km on Batesford Road. Free on-site parking makes it easy for shoppers who combine a class with a trip to Chaddy.",
  },
  {
    q: "Is Passion Pilates the closest boutique reformer studio to Malvern East?",
    a: "Yes. Passion Pilates is 2.5 km from Malvern East — approximately a 6-minute drive via Waverley Road. It is the closest 9-bed boutique reformer studio serving Malvern East, Oakleigh, Hughesdale and Murrumbeena.",
  },
  {
    q: "How much does a reformer Pilates class cost at Passion Pilates?",
    a: "Casual reformer Pilates classes at Passion Pilates are $35. New-member trials are $55 for 2 weeks unlimited (six classes). Foundation Memberships start from $35/week (student & senior) or $39/week (12-month standard). 10 Class Packs are $300 and 20 Class Packs are $560.",
  },
  {
    q: "What are the class times at Passion Pilates Chadstone?",
    a: "Weekdays: 6am, 7am, 9:30am, 10:30am, 5:30pm and 6:30pm classes. Weekends: 8:30am, 9:30am, 10:30am and 11:30am. All classes are 45 minutes. Studio is open 6am–8:30pm, 7 days a week.",
  },
  {
    q: "Does Passion Pilates offer classes for beginners?",
    a: "Yes. Foundation Reformer runs daily and is designed for absolute beginners — the tempo is slower, every exercise is explained, and springs are calibrated for a first-timer. Most new members start with the 2-week trial ($55) so they can try Foundation plus a mix of other class styles.",
  },
  {
    q: "Does Passion Pilates run Mums & Bubs Pilates classes?",
    a: "Yes. Mums & Bubs Reformer Pilates runs Tuesdays and Thursdays at Passion Pilates Chadstone. Babies from around 6 weeks old are welcome. Classes are postnatal-safe, diastasis-aware, and pelvic-floor first. Standard class rates and memberships apply.",
  },
  {
    q: "Where is Passion Pilates located exactly?",
    a: "Passion Pilates is at 5/49 Batesford Road, Chadstone, Victoria 3148, Australia. It is a 5-minute drive from Chadstone Shopping Centre, 7 minutes from Oakleigh, 6 minutes from Malvern East and 10 minutes from Mount Waverley. Phone 0498 451 148.",
  },
  {
    q: "What suburbs does Passion Pilates serve?",
    a: "Passion Pilates serves a 15 km radius around Chadstone, including Malvern East, Oakleigh, Mount Waverley, Carnegie, Ashwood, Ashburton, Glen Iris, Camberwell, Hughesdale, Murrumbeena, Notting Hill, Burwood, Hawthorn East, Kew, Toorak, South Yarra, Prahran, Bentleigh, McKinnon, Ormond, Elsternwick, Clayton, Huntingdale, Wheelers Hill, Glen Waverley and Box Hill.",
  },
  {
    q: "How do I book my first reformer Pilates class?",
    a: "Book online through our MindBody schedule at passionpilates.au. New members can claim the 2-week $55 introductory offer (6 reformer classes). All bookings are managed through the MindBody platform — there is no phone booking required.",
  },
  {
    q: "Is Passion Pilates good for advanced Pilates practitioners?",
    a: "Yes. Power Reformer (6:30pm weekdays) and Strength Reformer (heavier spring loading, longer time under tension) cater to intermediate and advanced practitioners. Foundation members typically rotate through all five class styles in a week.",
  },
  {
    q: "What makes Passion Pilates different from other reformer studios in Melbourne?",
    a: "Passion Pilates is boutique (only 9 beds) — the opposite of a warehouse studio. It runs five distinct reformer formats (not one repetitive style), Foundation Memberships lock in your rate for 12 months from $39/week, and it is one of the few south-east Melbourne studios with dedicated Mums & Bubs programming.",
  },
  {
    q: "Does Passion Pilates offer corporate bookings?",
    a: "Yes. Corporate reformer Pilates studio hire is available for teams of up to 10 — perfect for team-building, wellness days and private groups. Rates start from $315. Enquire via passion.chadstone@gmail.com.",
  },
  {
    q: "Can I freeze my Passion Pilates membership?",
    a: "Yes. Foundation Memberships include flexible freeze options: 1–26 weeks for medical, pregnancy or business relocation (certificate required); 1–12 weeks for holidays or personal reasons.",
  },
  {
    q: "Does Passion Pilates offer student or senior discounts?",
    a: "Yes. The Student & Senior Foundation Membership is $35/week (12-month term) — one of the lowest reformer Pilates rates in Melbourne. Valid student ID or senior status required.",
  },
];

const useDocumentMeta = ({ title, description, canonicalPath }) => {
  useEffect(() => {
    if (title) document.title = title;
    const setMeta = (selector, attrs) => {
      let el = document.querySelector(selector);
      if (!el) {
        el = document.createElement(attrs.tag);
        Object.entries(attrs.props).forEach(([k, v]) => el.setAttribute(k, v));
        document.head.appendChild(el);
      }
      if (attrs.content !== undefined) el.setAttribute("content", attrs.content);
      if (attrs.href !== undefined) el.setAttribute("href", attrs.href);
    };
    if (description) setMeta('meta[name="description"]', { tag: "meta", props: { name: "description" }, content: description });
    if (canonicalPath) setMeta('link[rel="canonical"]', { tag: "link", props: { rel: "canonical" }, href: `https://passionpilates.au${canonicalPath}` });
  }, [title, description, canonicalPath]);
};

export default function FAQPage() {
  useDocumentMeta({
    title: "Pilates FAQ — Chadstone Reformer Studio | Passion Pilates",
    description:
      "Every question people ask about reformer Pilates near Chadstone — pricing, class times, beginner classes, Mums & Bubs, memberships, service area and more.",
    canonicalPath: "/faq",
  });

  // FAQPage schema — this is what AI answer engines quote verbatim.
  const jsonLd = {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    mainEntity: FAQS.map((f) => ({
      "@type": "Question",
      name: f.q,
      acceptedAnswer: { "@type": "Answer", text: f.a },
    })),
  };

  return (
    <div
      className="min-h-screen bg-[#0a0a0a] text-[#E8DCC4]"
      style={{ fontFamily: "Georgia, 'Times New Roman', serif" }}
    >
      <JsonLd data={jsonLd} />

      <header className="border-b border-[#E8DCC4]/15">
        <div className="max-w-3xl mx-auto px-6 lg:px-10 py-8 flex items-center justify-between">
          <Link to="/" className="font-sans text-[11px] tracking-[0.3em] uppercase font-semibold text-[#C97654] hover:text-[#E8DCC4] transition-colors">
            ← Home
          </Link>
          <span className="font-sans text-[11px] tracking-[0.3em] uppercase text-[#E8DCC4]/60">
            Passion Pilates
          </span>
        </div>
      </header>

      <section className="max-w-3xl mx-auto px-6 lg:px-10 pt-14 pb-8">
        <p className="font-sans text-[11px] tracking-[0.3em] uppercase text-[#C97654] mb-5">
          Everything you might ask
        </p>
        <h1 className="text-[#FBF6EC] text-[38px] md:text-[56px] leading-[1.02] tracking-[-1px] font-normal mb-6">
          Frequently <em className="italic text-[#C97654]">asked.</em>
        </h1>
      </section>

      <section className="max-w-3xl mx-auto px-6 lg:px-10 pb-16">
        <dl className="space-y-8">
          {FAQS.map((f, i) => (
            <div key={f.q} className="border-t border-[#E8DCC4]/15 pt-8" data-testid={`faq-item-${i}`}>
              <dt className="text-[#FBF6EC] text-[22px] md:text-[26px] leading-tight font-normal tracking-[-0.3px] mb-3">
                {f.q}
              </dt>
              <dd className="font-sans text-[16px] md:text-[17px] leading-[1.65] text-[#E8DCC4]/85">
                {f.a}
              </dd>
            </div>
          ))}
        </dl>
      </section>

      {/* CTA */}
      <section className="border-t border-[#E8DCC4]/15 py-14">
        <div className="max-w-3xl mx-auto px-6 lg:px-10">
          <p className="font-sans text-[11px] tracking-[0.3em] uppercase font-semibold text-[#C97654] mb-3">
            Ready when you are
          </p>
          <h3 className="text-[#FBF6EC] text-[28px] md:text-[36px] leading-tight font-normal tracking-[-0.5px] mb-6">
            Try 6 reformer classes for <em className="italic text-[#C97654]">$55.</em>
          </h3>
          <a
            href="https://clients.mindbodyonline.com/classic/ws?studioid=5751621&stype=41&sTG=23&prodId=100052"
            target="_blank"
            rel="noopener noreferrer"
            className="inline-block font-sans font-bold text-[13px] tracking-[0.25em] uppercase bg-[#8B2635] hover:bg-[#a03042] text-[#FBF6EC] px-8 py-4 rounded-sm transition-colors duration-300"
          >
            Claim the trial →
          </a>
        </div>
      </section>

      <footer className="border-t border-[#E8DCC4]/15 py-10">
        <div className="max-w-3xl mx-auto px-6 lg:px-10 flex flex-col md:flex-row items-start md:items-center justify-between gap-4">
          <p className="font-sans text-[12px] tracking-[0.2em] uppercase text-[#E8DCC4]/55">Passion Pilates · Chadstone</p>
          <Link to="/" className="font-sans text-[12px] tracking-[0.2em] uppercase font-bold text-[#E8DCC4] border-b-2 border-[#C97654] pb-[2px] hover:text-[#C97654] transition-colors">
            Back to home →
          </Link>
        </div>
      </footer>
    </div>
  );
}
